






























































































































import {
  computed, defineComponent, onMounted,
  ref, onUnmounted,
} from '@nuxtjs/composition-api';
import { useMutationObserver } from '@vueuse/core';
import type { PropType } from '@nuxtjs/composition-api';
import type { ProductVariant } from '~/types/commercetools';
import { productGetters } from '~/utils/ct';
import { useIsMounted } from '~/composables';
import { PRODUCT_GALLERY } from '~/constants/productGallery';
import { ARROW_LEFT_KEY_CODE, ARROW_RIGHT_KEY_CODE } from '~/constants/keyCodes';
import GalleryModal from '~/components/molecules/Product/GalleryModal.vue';
import ThreeSixtyModal from '~/components/molecules/Product/ThreeSixtyModal.vue';

import SimpleCarousel from '~/components/molecules/SimpleCarousel/SimpleCarousel.vue';
import PdpThumbnailCarousel from '~/components/molecules/PdpThumbnailCarousel/PdpThumbnailCarousel.vue';
import ImgView from '~/components/atoms/ImgView/ImgView.vue';
import TwicpicsPicture from '~/components/atoms/TwicpicsImage/TwicpicsPicture.vue';
import { SCHEMA_ITEM_PROP } from '~/constants/seo';
import { SimpleCarouselArrow } from '~/types/components/SimpleCarousel/SimpleCarouselArrows';
import { getProductVideo } from '~/helpers/product/getProductVideo';
import { getProduct360Image } from '~/helpers/product/getProduct360Image';
import PdpThumbnailCarouselMobile from
  '~/components/molecules/Product/PdpThumbnailCarouselMobile/PdpThumbnailCarouselMobile.vue';
import type { ProductProcessedAttributes } from '~/types/product/attribute/ProductProcessedAttributes';
import { ProductVariantExtended } from '~/types/product/ProductVariantExtended';
import { ProductDetail } from '~/types/product/attribute/ProductDetail';

export default defineComponent({
  name: 'ProductGallery',
  components: {
    GalleryModal,
    ImgView,
    TwicpicsPicture,
    SimpleCarousel,
    PdpThumbnailCarousel,
    ThreeSixtyModal,
    PdpThumbnailCarouselMobile,
  },
  props: {
    product: {
      type: Object as PropType<ProductVariant>,
      required: true,
    },
    attributesLocalized: {
      type: Object as PropType<ProductProcessedAttributes>,
      required: true,
    },
  },
  setup(props, { root: { $i18n } }) {
    const isGalleryModalOpen = ref<boolean>(false);
    const { localeProperties: { code: languageAndCountry } } = $i18n;
    const activeIndex = ref(0);
    const { isMounted } = useIsMounted();
    const htmlElement = computed(() => typeof document !== 'undefined' ? document.querySelector('html') : null);
    const isAbTastyVideoDisabled = ref(false);
    const isAbTastyGalleryWithThreeImages = ref(false);
    const isAbTastyThreeSixtyDisabled = ref(false);

    const handleGalleryPrevNextArrows = (e: KeyboardEvent) => {
      const isLeftOrRightArrowNotPressed = e.keyCode !== ARROW_LEFT_KEY_CODE && e.keyCode !== ARROW_RIGHT_KEY_CODE;
      if (isLeftOrRightArrowNotPressed || !isGalleryModalOpen.value) return;
      e.preventDefault();
      e.keyCode === ARROW_LEFT_KEY_CODE ? handleLeftTouch() : handleRightTouch();
    };

    const setVideoDisabledAndGalleryThreeImages = () => {
      const hasDisableVideoClass = htmlElement.value?.classList.contains('ab-tasty-disable-video');
      const hasGalleryWithThreeImagesClass = htmlElement.value?.classList.contains('ab-tasty-gallery-three-images');
      const hasThreeSixtyClass = htmlElement.value?.classList.contains('ab-tasty-three-sixty-disabled');
      if (hasDisableVideoClass) {
        isAbTastyVideoDisabled.value = true;
      }
      if (hasGalleryWithThreeImagesClass) {
        isAbTastyGalleryWithThreeImages.value = true;
      }
      if (hasThreeSixtyClass) {
        isAbTastyThreeSixtyDisabled.value = true;
      }
    };

    useMutationObserver(htmlElement, (mutationsList) => {
      for (const mutation of mutationsList) {
        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
          setVideoDisabledAndGalleryThreeImages();
        }
      }
    }, {
      attributes: true,
      attributeFilter: ['class'],
    });

    onMounted(() => {
      window.addEventListener('keydown', handleGalleryPrevNextArrows);
      setVideoDisabledAndGalleryThreeImages();
    });
    onUnmounted(() => {
      window.removeEventListener('keydown', handleGalleryPrevNextArrows);
    });

    const handleLeftTouch = () => {
      if (activeIndex.value === totalSlidesCount.value - 1) {
        activeIndex.value = 0;
      } else {
        activeIndex.value += 1;
      }
    };

    const handleRightTouch = () => {
      if (activeIndex.value === minIndex) {
        activeIndex.value = totalSlidesCount.value - 1;
      } else {
        activeIndex.value -= 1;
      }
    };

    const openGalleryModal = (index: number) => {
      activeIndex.value = index;
      isGalleryModalOpen.value = true;
    };

    const closeGalleryModal = () => {
      isGalleryModalOpen.value = false;
    };

    const technicalDetails = computed<ProductDetail[]>(() => props.attributesLocalized?.techdetails || []);

    const getImageAlt = (product: ProductVariantExtended, index: number): string => {
      const productName = product._name || 'product';
      if (index === 0) {
        return productName;
      }

      const technicalDetail = technicalDetails.value[index - 1];
      const technicalDetailKey = technicalDetail?.key;
      const technicalDetailValue = technicalDetail?.value || technicalDetail?.values?.[0];
      if (!technicalDetailKey || !technicalDetailValue) {
        return `${productName} (${index})`;
      }

      const sufix = ` (${technicalDetail.key}: ${technicalDetail.value || technicalDetail.values[0]})`;
      return `${productName}${sufix}`;
    };

    const mappedImages = computed(() => productGetters.getGallery(props.product).map((img, index) => ({
      mobile: { url: img.small },
      desktop: { url: img.normal },
      big: { url: img.big },
      alt: getImageAlt(props.product, index),
      originalIndex: index,
    })));

    const images = computed(() =>
      isAbTastyGalleryWithThreeImages.value ? mappedImages.value.slice(0, 3) : mappedImages.value,
    );

    const videoInformation = computed(() => getProductVideo(props.product, languageAndCountry));

    const totalSlidesCount = computed(() => videoDetails.value ? images.value.length + 1 : images.value.length);

    const getImageBulletClass = (index: number, activeIndex: number): string => {
      return index === activeIndex ? 'iconify svg--circle-full text-EXPONDO-primary-80' : 'iconify svg--circle-empty';
    };

    const onClickMoreImages = () => {
      isGalleryModalOpen.value = true;
    };

    const productTitle = computed(() => productGetters.getName(props.product));
    const videoDetails = computed(() => {
      if (!videoInformation.value || isAbTastyVideoDisabled.value) {
        return null;
      }
      const videoLink = videoInformation.value.link;
      const videoThumbnailLink = videoInformation.value.thumbnail;
      return {
        mobile: {
          url: videoThumbnailLink,
        },
        desktop: {
          url: videoThumbnailLink,
        },
        big: {
          url: videoThumbnailLink,
        },
        play: {
          url: videoLink,
        },
        alt: `${$i18n.t('video')} - ${productTitle.value}`,
      };
    });

    const minIndex = 0;
    const onActiveImgChange = (index: number) => {
      if (index < minIndex || index > totalSlidesCount.value) {
        return;
      }
      openGalleryModal(index);
    };
    const desktopArrowClass = {
      container: 'h-12 cursor-pointer absolute z-1 top-carouselControl flex items-center',
      arrow: 'text-EXPONDO-black text-2xl',
    };

    const threeSixtyImage = computed(() => getProduct360Image(props.product));
    const showThreeSixtyGalleryButton = computed(() => {
      return !!(threeSixtyImage.value && !isAbTastyThreeSixtyDisabled.value);
    });
    const productSku = computed(() => props.product.sku);
    const isThreeSixtyModalOpen = ref<boolean>(false);

    const openThreeSixtyModal = () => {
      isThreeSixtyModalOpen.value = true;
    };

    const closeThreeSixtyModal = () => {
      isThreeSixtyModalOpen.value = false;
    };

    const videoImageUrl = computed(() =>
      (videoDetails.value && videoDetails.value.mobile && videoDetails.value.mobile.url) || '',
    );

    const videoTitle = computed(() => (videoDetails.value && videoDetails.value.alt) || '');

    return {
      isGalleryModalOpen,
      openGalleryModal,
      closeGalleryModal,
      onClickMoreImages,
      videoDetails,
      activeIndex,
      PRODUCT_GALLERY,
      totalSlidesCount,
      getImageBulletClass,
      onActiveImgChange,
      minIndex,
      handleLeftTouch,
      handleRightTouch,
      desktopArrowClass,
      images,
      SCHEMA_ITEM_PROP,
      isMounted,
      SimpleCarouselArrow,
      threeSixtyImage,
      showThreeSixtyGalleryButton,
      isThreeSixtyModalOpen,
      openThreeSixtyModal,
      closeThreeSixtyModal,
      productSku,
      videoImageUrl,
      videoTitle,
    };
  },
});

